import { MouseEvent as ReactMouseEvent, useCallback, useEffect, useRef } from "react";

const SHIFT_KEY = "Shift";
const CAROUSEL_TRAY_SELECTOR = ".carousel__slider-tray";

export const useShiftDrag = (enabled: boolean) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const isDraggingRef = useRef(false);
	const pointerPositionRef = useRef({ screenX: 0, screenY: 0 });

	const stopDrag = useCallback(() => {
		if (!isDraggingRef.current) {
			return;
		}

		isDraggingRef.current = false;
		document.documentElement.dispatchEvent(new MouseEvent("mouseup", { bubbles: true, cancelable: true }));
	}, []);

	const startDrag = useCallback(() => {
		if (isDraggingRef.current || !containerRef.current?.matches(":hover")) {
			return;
		}

		const tray = containerRef.current.querySelector<HTMLElement>(CAROUSEL_TRAY_SELECTOR);
		if (!tray) {
			return;
		}

		isDraggingRef.current = true;
		tray.dispatchEvent(
			new MouseEvent("mousedown", {
				bubbles: true,
				cancelable: true,
				button: 0,
				...pointerPositionRef.current,
			})
		);
	}, []);

	useEffect(() => {
		if (!enabled) {
			return;
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === SHIFT_KEY && !event.repeat) {
				startDrag();
			}
		};
		const handleKeyUp = (event: KeyboardEvent) => {
			if (event.key === SHIFT_KEY) {
				stopDrag();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		window.addEventListener("keyup", handleKeyUp);
		window.addEventListener("blur", stopDrag);

		return () => {
			window.removeEventListener("keydown", handleKeyDown);
			window.removeEventListener("keyup", handleKeyUp);
			window.removeEventListener("blur", stopDrag);
			stopDrag();
		};
	}, [enabled, startDrag, stopDrag]);

	const handleMouseEnter = (event: ReactMouseEvent<HTMLDivElement>) => {
		pointerPositionRef.current = { screenX: event.screenX, screenY: event.screenY };
		if (enabled && event.shiftKey) {
			startDrag();
		}
	};

	const handleMouseMove = (event: ReactMouseEvent<HTMLDivElement>) => {
		pointerPositionRef.current = { screenX: event.screenX, screenY: event.screenY };
	};

	return { containerRef, handleMouseEnter, handleMouseMove };
};
